import {
  LoaderCircle,
  Save,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from '../abastecimiento/ModalBase'
import { asociarDocumentoPagoCompra } from '../../services/cuentasPorPagar'
import {
  LIMITE_NUMERO_DOCUMENTO,
  respaldoRequiereNumero,
  TIPOS_RESPALDO,
  validarDocumentoPago,
} from '../../utils/cuentasPorPagar'

// Asocia o corrige el comprobante de un pago ya registrado. Supabase
// conserva el historial de cambios. No genera ninguna factura tributaria.
function AsociarDocumentoModal({
  pago,
  empresaId,
  onCerrar,
  onActualizado,
}) {
  const [tipoRespaldo, setTipoRespaldo] = useState('FACTURA')
  const [numeroDocumento, setNumeroDocumento] = useState('')
  const [mostrarErrores, setMostrarErrores] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState('')
  const enCursoRef = useRef(false)

  useEffect(() => {
    setTipoRespaldo(
      pago?.tipo_respaldo &&
        !['SIN_DOCUMENTO', 'DOCUMENTO_PENDIENTE'].includes(pago.tipo_respaldo)
        ? pago.tipo_respaldo
        : 'FACTURA'
    )
    setNumeroDocumento(pago?.numero_documento || '')
    setMostrarErrores(false)
    setErrorEnvio('')
    enCursoRef.current = false
  }, [pago?.id, pago?.tipo_respaldo, pago?.numero_documento])

  if (!pago) return null

  const validacion = validarDocumentoPago({
    tipoRespaldo,
    numeroDocumento,
  })
  const errores = mostrarErrores ? validacion.errores : {}

  const guardar = async (event) => {
    event.preventDefault()

    if (enCursoRef.current) return

    setMostrarErrores(true)

    if (!validacion.valido) return

    enCursoRef.current = true
    setEnviando(true)
    setErrorEnvio('')

    try {
      await asociarDocumentoPagoCompra({
        empresaId,
        pagoId: pago.id,
        tipoRespaldo,
        numeroDocumento,
      })

      onActualizado?.()
    } catch (errorAsociar) {
      setErrorEnvio(
        errorAsociar.message || 'No fue posible asociar el documento.'
      )
      enCursoRef.current = false
    } finally {
      setEnviando(false)
    }
  }

  return (
    <ModalBase
      abierto
      titulo="Asociar documento al pago"
      tituloId="titulo-asociar-documento"
      anchoMaximo="max-w-lg"
      procesando={enviando}
      onCerrar={onCerrar}
    >
      <form
        onSubmit={guardar}
        noValidate
        className="mt-5 space-y-4"
      >
        <CampoModal
          label="Tipo de respaldo"
          htmlFor="asociar-tipo"
          requerido
        >
          <select
            id="asociar-tipo"
            value={tipoRespaldo}
            disabled={enviando}
            onChange={(event) => setTipoRespaldo(event.target.value)}
            className={estiloInputModal}
          >
            {TIPOS_RESPALDO.map((respaldo) => (
              <option
                key={respaldo.valor}
                value={respaldo.valor}
              >
                {respaldo.etiqueta}
              </option>
            ))}
          </select>
          {errores.tipoRespaldo && (
            <p className="mt-1 text-sm text-red-600">
              {errores.tipoRespaldo}
            </p>
          )}
        </CampoModal>

        <CampoModal
          label="N° de documento"
          htmlFor="asociar-numero"
        >
          <input
            id="asociar-numero"
            type="text"
            maxLength={LIMITE_NUMERO_DOCUMENTO}
            value={numeroDocumento}
            disabled={enviando || !respaldoRequiereNumero(tipoRespaldo)}
            onChange={(event) => setNumeroDocumento(event.target.value)}
            className={estiloInputModal}
          />
          {errores.numeroDocumento && (
            <p className="mt-1 text-sm text-red-600">
              {errores.numeroDocumento}
            </p>
          )}
        </CampoModal>

        <p className="text-xs text-slate-500">
          Solo registra el comprobante del pago; no genera una factura
          tributaria. Los cambios quedan en el historial de auditoría.
        </p>

        {errorEnvio && <AvisoModal tipo="error">{errorEnvio}</AvisoModal>}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="min-h-12 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={enviando}
            className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enviando ? (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            ) : (
              <Save size={18} />
            )}
            Guardar documento
          </button>
        </div>
      </form>
    </ModalBase>
  )
}

export default AsociarDocumentoModal
